import { useState, useEffect } from 'react'
import { Plus, Key, MoreVertical, Download, Upload } from 'lucide-react'
import { Button } from '@quest-lock/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@quest-lock/ui/dropdown-menu'
import { AuthenticatorEntry, AuthenticatorStats } from '@/authenticator/Types'
import AuthenticatorItem from './AuthenticatorItem'
import AddAuthenticatorModal from './AddAuthenticatorModal'
import { toast } from 'sonner'
import { AuthenticatorService } from '@/authenticator/AuthenticatorService'
import { TOTPTimerProvider } from './TOTPTimerContext'

interface AuthenticatorListProps {
  onStartLocking?: (authenticatorId: string) => void
  onViewQuests?: (authenticatorId: string) => void
}

const AuthenticatorList = ({
  onStartLocking,
  onViewQuests
}: AuthenticatorListProps) => {
  const [authenticators, setAuthenticators] = useState<AuthenticatorEntry[]>([])
  const [stats, setStats] = useState<AuthenticatorStats>({
    total: 0,
    locked: 0,
    unlocked: 0
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [authenticatorService] = useState(() => new AuthenticatorService())

  useEffect(() => {
    initializeAndLoad()
  }, [])

  const initializeAndLoad = async () => {
    try {
      await authenticatorService.initialize()
      await loadAuthenticators()
    } catch (error) {
      console.error('Failed to initialize authenticator manager:', error)
      toast.error('Failed to load authenticators')
    } finally {
      setIsLoading(false)
    }
  }

  const loadAuthenticators = async () => {
    try {
      const [authList, authStats] = await Promise.all([
        authenticatorService.getAuthenticators(),
        authenticatorService.getStats()
      ])

      setAuthenticators(authList)
      setStats(authStats)
    } catch (error) {
      console.error('Failed to load authenticators:', error)
      toast.error('Failed to load authenticators')
    }
  }

  const handleAddAuthenticator = async (
    name: string,
    secret: string,
    issuer?: string,
    account?: string
  ) => {
    try {
      await authenticatorService.addAuthenticator(name, secret, issuer, account)
      await loadAuthenticators()
      setIsAddModalOpen(false)
      toast.success('Authenticator added successfully')
    } catch (error) {
      console.error('Failed to add authenticator:', error)
      toast.error('Failed to add authenticator')
    }
  }

  const handleLockAuthenticator = (id: string) => {
    onStartLocking?.(id)
  }

  const handleDeleteAuthenticator = async (id: string) => {
    try {
      await authenticatorService.deleteAuthenticator(id)
      await loadAuthenticators()
      toast.success('Authenticator deleted')
    } catch (error) {
      console.error('Failed to delete authenticator:', error)
      toast.error('Failed to delete authenticator')
    }
  }

  const handleRenameAuthenticator = async (id: string, newName: string) => {
    try {
      await authenticatorService.renameAuthenticator(id, newName)
      await loadAuthenticators()
    } catch (error) {
      console.error('Failed to rename authenticator:', error)
      toast.error('Failed to rename authenticator')
    }
  }

  const handleBackupAuthenticatorCodes = async () => {
    try {
      const data = await authenticatorService.exportData()

      const jsonString = JSON.stringify(data, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const url = URL.createObjectURL(blob)

      try {
        const link = document.createElement('a')
        link.href = url
        link.download = `questlock-authenticator-backup-${new Date()
          .toISOString()
          .replace(/[:.]/g, '-')}.json`

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        toast.success(
          `Successfully backed up ${
            data.authenticators.length
          } authenticator code${data.authenticators.length === 1 ? '' : 's'}`
        )
      } finally {
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Backup failed:', error)
      toast.error(
        error instanceof Error
          ? `Backup failed: ${error.message}`
          : 'Authenticator backup failed'
      )
    }
  }

  const handleRestoreAuthenticatorCodes = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/json,.json'

    input.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        const text = await file.text()
        const importData = JSON.parse(text)

        const successCount = await authenticatorService.mergeData(importData)

        if (successCount > 0) {
          await loadAuthenticators()
          toast.success(
            `Successfully restored ${successCount} authenticator code${
              successCount === 1 ? '' : 's'
            }`
          )
        } else {
          toast.info('No new authenticators to restore')
        }
      } catch (error) {
        console.error('Restore failed:', error)
        toast.error(
          error instanceof Error
            ? `Restore failed: ${error.message}`
            : 'Invalid backup file format'
        )
      }
    }

    input.click()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="border-shire-sun mx-auto mb-4 size-12 animate-spin rounded-full border-b-2"></div>
          <p className="text-shire-bark">Loading authenticators...</p>
        </div>
      </div>
    )
  }

  return (
    <TOTPTimerProvider>
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-between sm:flex-row">
          <div className="text-center">
            <h2 className="font-heading text-shire-dark text-2xl font-bold">
              Your Authenticators
            </h2>
            <p className="text-shire-bark/70">
              {stats.total > 0 &&
                `${stats.total} total • ${stats.unlocked} unlocked • ${stats.locked} locked`}
            </p>
          </div>

          {authenticators.length > 0 && (
            <div className="mt-4 flex gap-2 sm:mt-0">
              <Button onClick={() => setIsAddModalOpen(true)} size="md">
                <Plus className="mr-2 size-4" />
                Add Authenticator
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="md">
                    <MoreVertical className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleBackupAuthenticatorCodes}>
                    <Download className="mr-2 size-4" />
                    Backup Codes
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleRestoreAuthenticatorCodes}>
                    <Upload className="mr-2 size-4" />
                    Restore Codes
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {authenticators.length === 0 ? (
          <div className="py-12 pt-2 text-center">
            <div className="bg-shire-bark/10 mx-auto mb-6 flex size-24 items-center justify-center rounded-full">
              <Key className="text-shire-bark/50 size-12" />
            </div>
            <h3 className="font-heading text-shire-dark mb-2 text-xl font-semibold">
              No Authenticators Yet
            </h3>
            <p className="text-shire-bark/70 mx-auto mb-6 max-w-md">
              Add your first 2FA authenticator by scanning a QR code or entering
              the secret manually. Once added, you can lock codes behind quests
              for added security.
            </p>
            <Button onClick={() => setIsAddModalOpen(true)} size="lg">
              <Plus className="mr-2 size-5" />
              Add Your First Authenticator
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {authenticators.map(authenticator => (
              <AuthenticatorItem
                key={authenticator.id}
                authenticator={authenticator}
                onLock={handleLockAuthenticator}
                onDelete={handleDeleteAuthenticator}
                onRename={handleRenameAuthenticator}
                onViewQuests={onViewQuests}
              />
            ))}
          </div>
        )}

        <AddAuthenticatorModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddAuthenticator}
          existingSecrets={authenticators
            .map(a => a.secret)
            .filter(s => s !== undefined)}
        />
      </div>
    </TOTPTimerProvider>
  )
}

export default AuthenticatorList
